import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  Chip,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  SportsMma as SportsIcon,
  LocationOn as LocationOnIcon,
  ArrowBack as ArrowBackIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';

interface ScheduleLine {
  days: string;
  times: string;
  category?: string;
}

interface Venue {
  name: string;
  address: string;
  mapQuery: string;
  schedule: ScheduleLine[];
}

const venues: Venue[] = [
  {
    name: 'Aréna Törökbálint',
    address: '2045 Törökbálint, Torbágy utca 1. (Auchan területén)',
    mapQuery: 'Aréna Törökbálint, Torbágy utca 1, Törökbálint',
    schedule: [
      { days: 'Hétfő', times: '16:00–17:00, 17:00–18:00', category: 'kezdő gyerek' },
      { days: 'Hétfő', times: '18:00–20:00', category: 'haladó felnőtt' },
      { days: 'Kedd', times: '16:00–18:00', category: 'haladó gyerek' },
      { days: 'Kedd', times: '18:00–20:00', category: 'kezdő/középhaladó felnőtt' },
      { days: 'Csütörtök', times: '15:00–16:00, 16:00–17:00, 17:00–18:00', category: 'kezdő gyerek' },
      { days: 'Csütörtök', times: '18:00–20:00', category: 'haladó felnőtt' },
      { days: 'Péntek', times: '16:00–18:00', category: 'haladó gyerek' },
      { days: 'Péntek', times: '18:00–19:30', category: 'kezdő felnőtt' },
    ],
  },
  {
    name: 'MKOSZ Tornacsarnok Budaörs',
    address: '2040 Budaörs, Liget utca 12.',
    mapQuery: 'MKOSZ Tornacsarnok, Liget utca 12, Budaörs',
    schedule: [
      { days: 'Kedd, Csütörtök', times: '16:30–18:00', category: 'gyerek/ifjúsági' },
      { days: 'Kedd, Csütörtök', times: '18:00–20:00', category: 'felnőtt, edzővel' },
      { days: 'Péntek', times: '19:00–21:00', category: 'felnőtt, edző nélkül' },
      { days: 'Szombat', times: '10:00–12:00', category: 'gyerek/ifjúsági' },
    ],
  },
  {
    name: 'Budakeszi Amatőr Asztalitenisz SE',
    address: '2092 Budakeszi, Széchenyi István Általános Iskola, Árpád fejedelem tér 1.',
    mapQuery: 'Széchenyi István Általános Iskola, Árpád fejedelem tér 1, Budakeszi',
    schedule: [
      { days: 'Hétfő, Szerda', times: '16:00–18:00' },
    ],
  },
  {
    name: 'Biatorbágyi Innovatív Technikum (BIT)',
    address: '2051 Biatorbágy, Antall József utca 1.',
    mapQuery: 'Biatorbágyi Innovatív Technikum, Antall József utca 1, Biatorbágy',
    schedule: [
      { days: 'Kedd', times: '19:30–21:00', category: 'ifjúsági/felnőtt' },
    ],
  },
  {
    name: 'Érdi Teleki Sámuel Általános Iskola',
    address: '2030 Érd, Burkoló utca 40.',
    mapQuery: 'Teleki Sámuel Általános Iskola, Burkoló utca 40, Érd',
    schedule: [
      { days: 'Hétfő, Szerda', times: '16:00–17:30', category: 'gyerek' },
      { days: 'Hétfő, Szerda', times: '18:00–20:00', category: 'felnőtt' },
    ],
  },
];

const VenuesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <SportsIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            BK Pingpong
            <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.8, display: { xs: 'none', sm: 'inline' } }}>
              – Az oldal fejlesztés alatt, hamarosan elérhetővé válik...
            </Typography>
          </Typography>
          <ThemeToggle />
          <Button
            color="inherit"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/')}
            sx={{ ml: 0.5, minWidth: 'auto', px: { xs: 1, sm: 2 } }}
            size="small"
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Regisztráció</Box>
          </Button>
          <Button
            color="inherit"
            startIcon={<LoginIcon />}
            onClick={() => navigate('/')}
            sx={{ ml: 0.5, minWidth: 'auto', px: { xs: 1, sm: 2 } }}
            size="small"
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Bejelentkezés</Box>
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Vissza a főoldalra
        </Button>

        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          <LocationOnIcon sx={{ verticalAlign: 'middle', mr: 2, fontSize: 'inherit' }} />
          Helyszínek és Edzésidőpontok
        </Typography>
        <Divider sx={{ mb: 4 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {venues.map((venue) => (
            <Card key={venue.name} elevation={3}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Details */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
                      {venue.name}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 3 }}
                    >
                      <LocationOnIcon sx={{ mt: 0.3, flexShrink: 0 }} />
                      {venue.address}
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Edzésidőpontok
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {venue.schedule.map((line, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="text.primary">
                            <strong>{line.days}:</strong> {line.times}
                          </Typography>
                          {line.category && (
                            <Chip
                              label={line.category}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ height: 22, fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Grid>

                  {/* Map */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box
                      sx={{
                        width: '100%',
                        height: { xs: 250, md: '100%' },
                        minHeight: 250,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <iframe
                        title={`${venue.name} térkép`}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(venue.mapQuery)}&output=embed&hl=hu`}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'primary.dark',
          color: 'white',
          py: 4,
          mt: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                Elérhetőségek
              </Typography>
              <Typography variant="body2">
                Hajduska Rita: 06 70 40 96 996
              </Typography>
              <Typography variant="body2">
                Email: pingpongozzunk@gmail.com
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <a
                  href="https://www.facebook.com/pingpongHajduskaRita/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'white', textDecoration: 'underline' }}
                >
                  Facebook: pingpongHajduskaRita
                </a>
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
          <Typography variant="body2" align="center">
            © {new Date().getFullYear()} BK Pingpong. Minden jog fenntartva.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default VenuesPage;
